/* global I18n */

import React, {useState, useEffect} from "react"
import signals from "signals"
import QuizModeSelectorModal from
  "../quiz_mode_selector/quiz_mode_selector_modal"
import FirstQuizCelebration from "./first_quiz_celebration"
import StartQuizButton from "./start_quiz_button"
import VisitorCTA from "./visitor_cta"
import PreviewCTA from "./preview_cta"
import QuizSummary from "../quiz_summary/quiz_summary"
import QuizContentInfo from "./quiz_content_info"
import QuizDescription from "./quiz_description"
import QuizPreviewBanner from "./quiz_preview_banner"
import start_trainer from "../../start_trainer"
import FaIcon from "../fa_icon"
import {use_modal} from "../../modal_provider"

function EmbeddedQuiz({
  quiz,
  tracking_data,
  quiz_just_finished
}) {
  const {open_modal} = use_modal()
  const [initial_quiz_mode, set_initial_quiz_mode] = useState("study_mode")
  const [show_coverage, set_show_coverage] = useState(false)
  const [exam_conditions, set_exam_conditions] = useState(false)
  const description = quiz.quiz_description
  const cleaned_description = (
    description.slice(-1) === "." ? description.slice(0, -1) : description
  )
  const [current_view, set_current_view] = useState(choose_initial_view())

  function choose_initial_view() {
    if (!quiz_just_finished) return create_main_view()
    if (!quiz.can_see_quiz_stats) return <VisitorCTA />
    if (!quiz.can_train) return <PreviewCTA on_close={show_main_view} />

    return create_main_view()
  }

  function show_main_view() {
    set_current_view(create_main_view())
  }

  function create_main_view() {
    return (
      <>
        {!!quiz.training_sessions_count &&
          <>
            {quiz_just_finished && quiz.total_training_sessions_count === 1 &&
              <FirstQuizCelebration />}
            <QuizSummary
              description={cleaned_description}
              last_training_in_words={quiz.last_training_in_words}
              exam_training_stats={quiz.exam_training_stats}
              study_training_stats={quiz.study_training_stats}
              can_train={quiz.can_train}
              open_quiz_mode_modal={open_quiz_mode_modal}
              revise_exam_quiz={revise_exam_quiz}
              on_summary_mode_change={set_initial_quiz_mode}
            />
          </>}
        {!quiz.training_sessions_count &&
          <QuizModeSelectorModal
            quiz_data={quiz}
            inline={true}
            tracking_data={tracking_data}
            initial_quiz_mode={initial_quiz_mode}
            summary_path={`${window.location.pathname}?summary=quiz-${quiz.id}`}
            exam_conditions={exam_conditions}
          />}
      </>
    )
  }

  function revise_exam_quiz(quiz_mode, configuration) {
    start_trainer(
      quiz,
      quiz_mode,
      configuration,
      tracking_data,
      window.location.pathname
    )
  }

  function open_quiz_mode_modal(mode, exam_condition = false) {
    if (mode) set_initial_quiz_mode(mode)
    set_exam_conditions(exam_condition)
    open_modal("quiz_mode_selector")
  }

  use_summary_tracking(current_view, quiz_just_finished, tracking_data)

  const disable_button = [
    PreviewCTA,
    VisitorCTA
  ].includes(current_view.type)

  return (
    <div className={`embedded-quiz ${!quiz.can_train ? "preview" : ""}`}>
      <div className="embedded-quiz__header">
        <div className="embedded-quiz__image spinner">
          <img alt="" crossOrigin="anonymous" src={quiz.quiz_image} />
        </div>
        <div className="embedded-quiz__info">
          {!quiz.can_train &&
            <div className="badge badge--small badge--red preview-tag">
              <FaIcon name={"fa-lock"}/>
              {I18n.t("js.quiz_mode_selector.preview.preview_mode")}
            </div>
          }
          <QuizDescription
            description={cleaned_description}
            has_been_quizzed={quiz.training_sessions_count}
            last_training_in_words={quiz.last_training_in_words}
            can_train={quiz.can_train}
            show_coverage={show_coverage}
            set_show_coverage={set_show_coverage}
            is_completed={quiz.is_completed}
            completed_at={quiz.completed_at}
            score={quiz.score}
          />
          {!!quiz.training_sessions_count && <StartQuizButton
            disabled={disable_button}
            tracking_data={tracking_data}
            on_click={() => open_quiz_mode_modal()}
          />}
        </div>
      </div>
      {!quiz.can_train && <QuizPreviewBanner/>}
      <div className="embedded-quiz__main">
        {show_coverage &&
          <QuizContentInfo
            learnables={quiz.learnables}
            description={cleaned_description}
          />}
        {current_view}
      </div>
      {!!quiz.training_sessions_count &&
        <QuizModeSelectorModal
          quiz_data={quiz}
          inline={false}
          tracking_data={tracking_data}
          initial_quiz_mode={initial_quiz_mode}
          summary_path={`${window.location.pathname}?summary=quiz-${quiz.id}`}
          exam_conditions={exam_conditions}
        />}
    </div>
  )
}

function use_summary_tracking(current_view, quiz_just_finished, tracking_data) {
  useEffect(() => {
    if (!quiz_just_finished) return
    signals.emit("kenhub.quiz_summary_seen", {
      element: current_view.type.displayName, ...tracking_data
    })
  }, [quiz_just_finished])
}

export default EmbeddedQuiz
